import './kp-pdf-viewer.less';

import type {IOnDestroy, IOnInit, ITimeoutService} from 'angular';
import type {Component, OnChanges, OnChangesObject} from 'typings/portaro.fe.types';
import {ngComponentCssClass} from 'shared/utils/ng-@decorators';
import type KpPdfViewerControlPanelService from '../kp-pdf-viewer-control-panel/kp-pdf-viewer-control-panel.service';
import type PdfProxiedCommands from '../kp-pdf-viewer-control-panel/pdf-proxied-commands';

function floor(value, precision) {
    const multiplier = Math.pow(10, precision || 0);
    return Math.floor(value * multiplier) / multiplier;
}

export abstract class KpPdfViewerComponentController implements KpPdfViewerComponentBindings, IOnInit, OnChanges<KpPdfViewerComponentBindings>, IOnDestroy {
    public pdfControlId: string;
    public fileUrl: string;
    public fileSize: number | null;
    public currentPage: number;
    public scale: number;
    public fit: PdfViewerFit;

    public abstract onCurrentPageChange(locals: { $currentPage: number });

    public abstract onScaleChange(locals: { $scale: number });

    public abstract onFitChange(locals: { $fit: PdfViewerFit });

    public abstract onPageCountChange(locals: { $pageCount: number });

    public abstract onDocumentRendered(locals: { $info: any; });

    public abstract onDocumentErrored(locals: { $error: any; });

    public optimalScale: number;
    public pageCount: number;
    public isPreviewEnabled: boolean;
    public internalScale: PdfScale;
    private pdfControlPanelProxy: PdfProxiedCommands;

    /*@ngInject*/
    constructor(private $timeout: ITimeoutService, private pdfViewerControlPanelService: KpPdfViewerControlPanelService) {
    }

    public $onInit(): void {
        if (!this.fit) {
            this.fit = 'width';
        }

        this.internalScale = {value: 1, selfChange: true};

        if (this.pdfControlId) {
            this.pdfControlPanelProxy = this.pdfViewerControlPanelService.registerPdfViewerController(this.pdfControlId, this);
        }
    }

    public $onChanges(onChangesObj: OnChangesObject<KpPdfViewerComponentBindings>): void {

        if (onChangesObj.currentPage && onChangesObj.currentPage.currentValue && onChangesObj.currentPage.isFirstChange()) {
            // From unknown and for me very weird reason binding of currentPage attribute in $onInit phase is undefined,
            // although here is bound correctly. So this is workaround for correct setting initial value of currentPage
            const currentPage = onChangesObj.currentPage.currentValue;
            this.$timeout(() => {
                this.currentPage = currentPage;
                this.currentPageChanged(currentPage);
            }, 0);
        }

        if (onChangesObj.fileUrl && onChangesObj.fileUrl.currentValue) {
            this.currentPage = undefined;
        }

        if (onChangesObj.scale && onChangesObj.scale.currentValue && this.scale !== this.internalScale.value) {
            this.updateScale(onChangesObj.scale.currentValue);
        }
    }

    public $onDestroy(): void {
        this.pdfViewerControlPanelService.unregisterPdfViewerController(this.pdfControlId);
    }

    public updateScale(scale: number, isOptimal: boolean = false, selfChange: boolean = false) {
        const roundedScale = floor(scale, 2);

        if (scale === this.internalScale.value) {
            return;
        }

        if (isOptimal) {
            this.optimalScale = roundedScale;
        }

        this.internalScale = {
            value: roundedScale,
            selfChange
        };

        if (!selfChange) {
            this.updateFitChange('none');

        }

        this.onScaleChange({$scale: this.internalScale.value});
        this.callPdfControlPanel((controlPanel) => controlPanel.updateScale(roundedScale));
    }

    public pageCountChanged(pageCount: number) {
        this.onPageCountChange({$pageCount: pageCount});
        this.callPdfControlPanel((controlPanel) => controlPanel.setPageCount(pageCount));
    }

    public updateFitChange(fit: PdfViewerFit) {
        this.onFitChange({$fit: fit});
        this.callPdfControlPanel((controlPanel) => {
            switch (fit) {
                case 'auto':
                    controlPanel.fitAuto();
                    break;
                case 'width':
                    controlPanel.fitWidth();
                    break;
            }
        });
    }

    public currentPageChanged(currentPage: number) {
        this.onCurrentPageChange({$currentPage: currentPage});
        this.callPdfControlPanel((controlPanel) => controlPanel.setCurrentPage(currentPage));
    }

    private callPdfControlPanel(callback: (controlPanel?: PdfProxiedCommands) => any) {
        if (this.pdfControlId) {
            return callback(this.pdfControlPanelProxy);
        }
    }
}

/**
 * @ngdoc component
 * @name kpPdfViewer
 * @module pdf-viewer
 *
 * @param {string} fileUrl URL of pdf document.
 * @param {number} fileSize Size in bytes of pdf document.
 * @param {number} currentPage Desired page.
 * @param {number} scale Zoom scale.
 * @param {PdfViewerFit} fit Auto fit.
 * @param {string} pdfControlId ID of {@link component.kpPdfViewerControlPanel kp-pdf-viewer-control-panel}
 *
 * @param {function(number)} onCurrentPageChange Current page change callback.
 * @param {number} onCurrentPageChange.$currentPage Current page.
 * @param {function(number)} onScaleChange Scale change callback.
 * @param {number} onScaleChange.$scale Scale
 * @param {function(PdfViewerFit)} onFitChange Fit change callback.
 * @param {PdfViewerFit} onFitChange.$fit Fit settings.
 * @param {function(number)} onPageCountChange Page count change callback.
 * @param {number} onPageCountChange.$pageCount Page count.
 * @param {function(object)} onDocumentRendered Render error callback.
 * @param {object} onDocumentRendered.$info Error.
 * @param {function(object)} onDocumentErrored Document fetch error callback.
 * @param {object} onDocumentErrored.$error Error
 *
 * @description
 * PDF viewer.
 */
export default class KpPdfViewerComponent implements Component<KpPdfViewerComponentBindings> {
    public static componentName = 'kpPdfViewer';

    @ngComponentCssClass('pdf-viewer')
    public templateUrl = require('./kp-pdf-viewer.tpl.pug');

    public bindings = {
        pdfControlId: '@?',
        fileUrl: '<',
        fileSize: '<',
        currentPage: '<',
        scale: '<',
        fit: '<',
        onCurrentPageChange: '&',
        onScaleChange: '&',
        onFitChange: '&',
        onPageCountChange: '&',
        onDocumentRendered: '&?',
        onDocumentErrored: '&?'
    } as const;

    public controller = KpPdfViewerComponentController;
    public controllerAs = 'pdfViewerController';
}

interface KpPdfViewerComponentBindings {
    fileUrl: string;
    fileSize: number | null;
    currentPage: number;
    scale: number;
    fit: PdfViewerFit;
    pdfControlId?: string;

    onCurrentPageChange(locals: { $currentPage: number });

    onScaleChange(locals: { $scale: number });

    onFitChange(locals: { $fit: PdfViewerFit });

    onPageCountChange(locals: { $pageCount: number });

    onDocumentRendered(locals: { $info: any });

    onDocumentErrored(locals: { $error: any });
}

export type PdfViewerFit = 'width' | 'auto' | 'none';

export interface PdfScale {
    value: number;
    selfChange: boolean;
}
